// MultiplayerController — bridge orchestration for collaborative drawing.
//
// Sits between the monolith (window.JoeFlowCommerce.multiplayer) and the
// transport port. Its single added responsibility over the raw port is the
// ENTITLEMENT GATE: join() refuses when the user may not use multiplayer. For
// the MVP the gate is open (`canUse` returns true for everyone), but it is wired
// through commerce `canUse('multiplayer')` so flipping it to a paid/login gate
// later is a one-line change in the gating table — no rework here or in the
// monolith.
//
// Pure orchestration (depends only on the port + an injected gate): unit-tested
// with an in-memory fake transport.

import type { MultiplayerPort } from './ports';
import type {
  RemoteClear,
  RemoteStrokeEnd,
  RemoteStrokeStart,
  RemoteStrokeUpdate,
  RemoteUndo,
  StrokeStartInput,
  StrokeUpdateInput,
} from '../domain/multiplayer';

export interface MultiplayerControllerDeps {
  transport: MultiplayerPort;
  /** Entitlement gate — wraps commerce canUse('multiplayer'). */
  canUse: () => boolean;
}

export class MultiplayerController {
  private readonly transport: MultiplayerPort;
  private readonly gate: () => boolean;

  constructor(deps: MultiplayerControllerDeps) {
    this.transport = deps.transport;
    this.gate = deps.canUse;
  }

  /** True when the current entitlement permits collaborative drawing. */
  canUse(): boolean {
    return this.gate();
  }

  /**
   * Join a room. Resolves true on success, false when the entitlement gate
   * blocks it (the monolith should then surface the paywall). Never throws on
   * the gate path so the caller can branch on the boolean.
   */
  async join(roomId: string): Promise<boolean> {
    if (!this.gate()) return false;
    await this.transport.join(roomId);
    return true;
  }

  async leave(): Promise<void> {
    await this.transport.leave();
  }

  isActive(): boolean {
    return this.transport.isActive();
  }

  roomId(): string | null {
    return this.transport.roomId();
  }

  clientId(): string {
    return this.transport.clientId();
  }

  // ---- send (no-ops when not in a room; the transport guards) -------------
  sendStrokeStart(input: StrokeStartInput): void {
    this.transport.sendStrokeStart(input);
  }
  sendStrokeUpdate(input: StrokeUpdateInput): void {
    this.transport.sendStrokeUpdate(input);
  }
  sendStrokeEnd(strokeId: string): void {
    this.transport.sendStrokeEnd({ strokeId });
  }
  sendClear(): void {
    this.transport.sendClear();
  }
  sendUndo(strokeId: string): void {
    this.transport.sendUndo(strokeId);
  }

  // ---- subscribe (return unsubscribe handles) -----------------------------
  onStrokeStart(cb: (s: RemoteStrokeStart) => void): () => void {
    return this.transport.onStrokeStart(cb);
  }
  onStrokeUpdate(cb: (s: RemoteStrokeUpdate) => void): () => void {
    return this.transport.onStrokeUpdate(cb);
  }
  onStrokeEnd(cb: (s: RemoteStrokeEnd) => void): () => void {
    return this.transport.onStrokeEnd(cb);
  }
  onClear(cb: (c: RemoteClear) => void): () => void {
    return this.transport.onClear(cb);
  }
  onUndo(cb: (u: RemoteUndo) => void): () => void {
    return this.transport.onUndo(cb);
  }
  onParticipants(cb: (count: number) => void): () => void {
    return this.transport.onParticipants(cb);
  }
}
